import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Check, Flag, Trash2, ExternalLink } from "lucide-react";

const PLACEHOLDER = "https://images.unsplash.com/photo-1572521165329-b197f9ea3da6?crop=entropy&cs=srgb&fm=jpg&w=200&q=60";
const TABS = ["pending", "flagged", "active", "all"];

export default function AdminListings() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "pending";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    const s = status === "all" ? "" : `status=${status}`;
    api.get(`/admin/listings?${s}`).then(({ data }) => setItems(data.items || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const approve = async (id) => { await api.post(`/admin/listings/${id}/approve`); toast.success("Approved"); load(); };
  const flag = async (id) => {
    const note = window.prompt("Optional flag note:") || "";
    await api.post(`/admin/listings/${id}/flag`, { note });
    toast.success("Flagged"); load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    await api.delete(`/admin/listings/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="overline text-safety">Listings moderation</div>
      <h1 className="mt-1 font-display font-black text-3xl tracking-tighter text-forest">Listing queue</h1>

      <div className="mt-6 flex border border-border w-fit">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setParams({ status: t })}
            data-testid={`admin-listings-tab-${t}`}
            className={`px-5 h-10 text-xs overline transition-colors duration-200 ${status === t ? "bg-forest text-white" : "bg-white hover:bg-sage"}`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="mt-6 border border-border bg-white">
        {loading ? (
          <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">Nothing in "{status}"</div>
        ) : items.map((l, i) => (
          <div key={l.listing_id} data-testid={`admin-listing-row-${l.listing_id}`} className={`grid grid-cols-[80px,1fr,auto] items-center gap-4 p-4 ${i > 0 ? "border-t border-border" : ""}`}>
            <img src={l.images?.[0] ? fileUrl(l.images[0]) : PLACEHOLDER} alt="" onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} className="w-20 h-20 object-cover border border-border" />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={`rounded-none overline text-[10px] ${l.status === "pending" ? "bg-safety text-white" : l.status === "flagged" ? "bg-destructive text-white" : "bg-forest text-white"}`}>{l.status}</Badge>
                <div className="overline text-muted-foreground text-[10px]">{l.category} · £{Number(l.price).toFixed(2)}</div>
              </div>
              <div className="mt-1 font-display text-lg tracking-tight truncate">{l.title}</div>
              <div className="mt-1 text-xs text-muted-foreground truncate">Seller: {l.seller_name} · {l.location} · {new Date(l.created_at).toLocaleDateString("en-GB")}</div>
              {l.admin_note && <div className="mt-1 text-xs text-safety">Note: {l.admin_note}</div>}
            </div>
            <div className="flex items-center gap-2">
              <Button asChild variant="outline" size="sm" className="rounded-none">
                <Link to={`/listings/${l.listing_id}`} target="_blank" rel="noreferrer">
                  <ExternalLink className="w-4 h-4 mr-1" /> View
                </Link>
              </Button>
              {l.status !== "active" && (
                <Button onClick={() => approve(l.listing_id)} size="sm" data-testid={`admin-listing-approve-${l.listing_id}`} className="rounded-none bg-forest hover:bg-forest-800 text-white">
                  <Check className="w-4 h-4 mr-1" /> Approve
                </Button>
              )}
              {l.status !== "flagged" && (
                <Button onClick={() => flag(l.listing_id)} variant="outline" size="sm" data-testid={`admin-listing-flag-${l.listing_id}`} className="rounded-none border-safety text-safety hover:bg-safety hover:text-white">
                  <Flag className="w-4 h-4 mr-1" /> Flag
                </Button>
              )}
              <Button onClick={() => remove(l.listing_id)} variant="outline" size="sm" data-testid={`admin-listing-delete-${l.listing_id}`} className="rounded-none border-destructive text-destructive hover:bg-destructive hover:text-white">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
